import { createSelector } from "@reduxjs/toolkit";
import {
  ROOM_NUMBERING_METHODS,
  type IRoomNumbering,
  type IRoomSummary,
} from "@multiconsult-group/multiplacer-core";
import { selectHttpRoomsBasedOnActiveProjectTab } from "../Room/httpRoomSelectors";

export type IRoomNumberingExportRow = {
  projectNo: number;
  roomNumber: string;
  roomName: string;
  roomNumberingValue: string;
  roomNumberingValueSource: "manual" | "suggested" | "";
  suggestedRoomNumber: string;
  manualRoomNumber: string;
  isApproved: boolean;
  approvedBy: string;
  approvedAt: string;
  manualUpdatedBy: string;
  manualUpdatedAt: string;
  methodKey: string;
  methodTitle: string;
  methodCustomer: string;
  methodPdfFileName: string;
  methodSummary: string;
  runBy: string;
  runAt: string;
};

const normalizeRoomNumberingValue = (value: string | undefined): string => {
  const trimmed = value?.trim() ?? "";
  return trimmed === "-" ? "" : trimmed;
};

const formatDateTimeForExport = (value: string | Date | undefined): string => {
  if (value == null) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
};

const getRoomNumberingValue = (
  roomNumbering: IRoomNumbering | undefined
): Pick<IRoomNumberingExportRow, "roomNumberingValue" | "roomNumberingValueSource"> => {
  const manualValue = normalizeRoomNumberingValue(roomNumbering?.value);
  if (manualValue.length > 0) {
    return {
      roomNumberingValue: manualValue,
      roomNumberingValueSource: "manual",
    };
  }

  const suggestedValue = normalizeRoomNumberingValue(roomNumbering?.suggested);
  if (suggestedValue.length > 0) {
    return {
      roomNumberingValue: suggestedValue,
      roomNumberingValueSource: "suggested",
    };
  }

  return {
    roomNumberingValue: "",
    roomNumberingValueSource: "",
  };
};

const toRoomNumberingExportRow = (room: IRoomSummary): IRoomNumberingExportRow => {
  const roomNumbering = room.roomNumbering;
  const method = ROOM_NUMBERING_METHODS.find((item) => item.key === roomNumbering?.run?.methodKey);

  return {
    projectNo: room.projectNo,
    roomNumber: room.roomNumber,
    roomName: room.roomName ?? "",
    ...getRoomNumberingValue(roomNumbering),
    suggestedRoomNumber: normalizeRoomNumberingValue(roomNumbering?.suggested),
    manualRoomNumber: normalizeRoomNumberingValue(roomNumbering?.value),
    isApproved: roomNumbering?.approval != null,
    approvedBy: roomNumbering?.approval?.approvedBy.name ?? "",
    approvedAt: formatDateTimeForExport(roomNumbering?.approval?.approvedAt),
    manualUpdatedBy: roomNumbering?.updatedBy?.name ?? "",
    manualUpdatedAt: formatDateTimeForExport(roomNumbering?.updatedAt),
    methodKey: roomNumbering?.run?.methodKey ?? "",
    methodTitle: method?.title ?? "",
    methodCustomer: method?.customer ?? "",
    methodPdfFileName: method?.pdfFileName ?? "",
    methodSummary: method?.summary ?? "",
    runBy: roomNumbering?.run?.runBy.name ?? "",
    runAt: formatDateTimeForExport(roomNumbering?.run?.runAt),
  };
};

export const selectRoomNumberingExportRows = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsByRoomNumber): IRoomNumberingExportRow[] =>
    Object.values(roomsByRoomNumber)
      .map(toRoomNumberingExportRow)
      .sort((a, b) =>
        a.roomNumber.localeCompare(b.roomNumber, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
);
